"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useState } from "react";

import {
  applicationFormSchema,
  experienceOptions,
  noticePeriodOptions,
  type ApplicationFormSchemaData,
} from "@/lib/validation/careers.schema";
import { useToast } from "@/contexts/ToastContext";
import { submitApplicationToDiscord } from "@/lib/actions/careers.actions";
import { JobPosting } from "@/types/careers";
import VoiceRecorder from "./VoiceRecorder";
import { Upload, FileText, X } from "lucide-react";
import Link from "next/link";

interface ApplicationFormProps {
  job: JobPosting;
}

export default function ApplicationForm({ job }: ApplicationFormProps) {
  const [voiceBlob, setVoiceBlob] = useState<Blob | null>(null);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [voiceError, setVoiceError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    control,
  } = useForm<ApplicationFormSchemaData>({
    resolver: zodResolver(applicationFormSchema),
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      yearsOfExperience: "",
      relevantTools: "",
      expectedSalary: "",
      noticePeriod: "",
      privacyConsent: false,
    },
  });

  const { success, error } = useToast();

  const handleVoiceRecordingComplete = (blob: Blob) => {
    setVoiceBlob(blob);
    setVoiceError(null);
  };

  const handleResumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        error("Resume must be under 10MB");
        return;
      }
      // Validate file type
      const validTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!validTypes.includes(file.type)) {
        error("Resume must be PDF, DOC, or DOCX format");
        return;
      }
      setResumeFile(file);
    }
  };

  const removeResume = () => {
    setResumeFile(null);
  };

  const onSubmit = async (data: ApplicationFormSchemaData) => {
    try {
      // Validate voice recording
      if (
        job.applicationConfig.fields.voiceRecording.required &&
        !voiceBlob
      ) {
        setVoiceError("Voice recording is required");
        return;
      }

      // Build FormData for server action
      const formData = new FormData();
      formData.append("webhookUrl", job.applicationConfig.discordWebhookUrl);
      formData.append("jobTitle", job.metadata.title);
      formData.append("fullName", data.fullName);
      formData.append("email", data.email);
      formData.append("phone", data.phone);
      formData.append("yearsOfExperience", data.yearsOfExperience);
      formData.append("relevantTools", data.relevantTools);

      if (data.expectedSalary) {
        formData.append("expectedSalary", data.expectedSalary);
      }
      if (data.noticePeriod) {
        formData.append("noticePeriod", data.noticePeriod);
      }

      if (voiceBlob) {
        formData.append("voiceRecording", voiceBlob, "voice_recording.webm");
      }

      if (resumeFile) {
        formData.append("resume", resumeFile);
      }

      const result = await submitApplicationToDiscord(formData);

      if (result.success) {
        success("Application submitted successfully! We will be in touch soon.");
        reset();
        setVoiceBlob(null);
        setResumeFile(null);
      } else {
        error(result.error || "Failed to submit application. Please try again.");
      }
    } catch (err) {
      error(
        err instanceof Error
          ? err.message
          : "Failed to submit application. Please try again."
      );
    }
  };

  const { fields } = job.applicationConfig;

  return (
    <div className="bg-white p-6 md:p-8 rounded-lg border border-gray-200 shadow-lg">
      <h3 className="text-2xl font-bold mb-6 text-[#284D93]">
        Apply for this Position
      </h3>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Name and Phone Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {fields.fullName.enabled && (
            <div>
              <label
                htmlFor="fullName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Full Name {fields.fullName.required && "*"}
              </label>
              <input
                type="text"
                id="fullName"
                {...register("fullName")}
                className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#5378BC] focus:border-transparent transition-all ${
                  errors.fullName ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="John Doe"
              />
              {errors.fullName && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.fullName.message}
                </p>
              )}
            </div>
          )}

          {fields.phone.enabled && (
            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Phone Number {fields.phone.required && "*"}
              </label>
              <input
                type="tel"
                id="phone"
                {...register("phone")}
                className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#5378BC] focus:border-transparent transition-all ${
                  errors.phone ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="1234567890"
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.phone.message}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Email */}
        {fields.email.enabled && (
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email Address {fields.email.required && "*"}
            </label>
            <input
              type="email"
              id="email"
              {...register("email")}
              className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#5378BC] focus:border-transparent transition-all ${
                errors.email ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="john@example.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>
        )}

        {/* Years of Experience */}
        {fields.yearsOfExperience.enabled && (
          <div>
            <label
              htmlFor="yearsOfExperience"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Years of Experience {fields.yearsOfExperience.required && "*"}
            </label>
            <select
              id="yearsOfExperience"
              {...register("yearsOfExperience")}
              className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#5378BC] focus:border-transparent transition-all ${
                errors.yearsOfExperience ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select your experience level</option>
              {(fields.yearsOfExperience.options || experienceOptions.map(o => o.value)).map(
                (option) => (
                  <option key={option} value={option}>
                    {experienceOptions.find((o) => o.value === option)?.label ||
                      option}
                  </option>
                )
              )}
            </select>
            {errors.yearsOfExperience && (
              <p className="mt-1 text-sm text-red-600">
                {errors.yearsOfExperience.message}
              </p>
            )}
          </div>
        )}

        {/* Expected Salary */}
        {fields.expectedSalary?.enabled && (
          <div>
            <label
              htmlFor="expectedSalary"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Expected Salary {fields.expectedSalary.required && "*"}
            </label>
            <input
              type="text"
              id="expectedSalary"
              {...register("expectedSalary")}
              className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#5378BC] focus:border-transparent transition-all ${
                errors.expectedSalary ? "border-red-500" : "border-gray-300"
              }`}
              placeholder={
                fields.expectedSalary.placeholder || "e.g., $5,000/month"
              }
            />
            {errors.expectedSalary && (
              <p className="mt-1 text-sm text-red-600">
                {errors.expectedSalary.message}
              </p>
            )}
          </div>
        )}

        {/* Notice Period */}
        {fields.noticePeriod?.enabled && (
          <div>
            <label
              htmlFor="noticePeriod"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Notice Period {fields.noticePeriod.required && "*"}
            </label>
            <select
              id="noticePeriod"
              {...register("noticePeriod")}
              className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#5378BC] focus:border-transparent transition-all ${
                errors.noticePeriod ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select notice period</option>
              {(fields.noticePeriod.options || noticePeriodOptions.map(o => o.value)).map(
                (option) => (
                  <option key={option} value={option}>
                    {noticePeriodOptions.find((o) => o.value === option)?.label ||
                      option}
                  </option>
                )
              )}
            </select>
            {errors.noticePeriod && (
              <p className="mt-1 text-sm text-red-600">
                {errors.noticePeriod.message}
              </p>
            )}
          </div>
        )}

        {/* Relevant Tools */}
        {fields.relevantTools.enabled && (
          <div>
            <label
              htmlFor="relevantTools"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Relevant Tools & Skills {fields.relevantTools.required && "*"}
            </label>
            <textarea
              id="relevantTools"
              rows={4}
              {...register("relevantTools")}
              className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#5378BC] focus:border-transparent transition-all resize-none ${
                errors.relevantTools ? "border-red-500" : "border-gray-300"
              }`}
              placeholder={
                fields.relevantTools.placeholder ||
                "List the tools, technologies, and skills you are proficient in..."
              }
            />
            {errors.relevantTools && (
              <p className="mt-1 text-sm text-red-600">
                {errors.relevantTools.message}
              </p>
            )}
          </div>
        )}

        {/* Voice Recording */}
        {fields.voiceRecording.enabled && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Voice Recording {fields.voiceRecording.required && "*"}
            </label>
            <VoiceRecorder
              maxDuration={fields.voiceRecording.maxDuration || 120}
              prompt={fields.voiceRecording.prompt}
              onRecordingComplete={handleVoiceRecordingComplete}
              error={voiceError || undefined}
            />
            {voiceBlob && (
              <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                <FileText className="w-4 h-4" />
                Voice recording ready ({(voiceBlob.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>
        )}

        {/* Resume Upload */}
        {fields.resume.enabled && (
          <div>
            <label
              htmlFor="resume"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Resume {fields.resume.required ? "*" : "(Optional)"}
            </label>
            {!resumeFile ? (
              <div className="relative">
                <input
                  type="file"
                  id="resume"
                  accept=".pdf,.doc,.docx"
                  onChange={handleResumeChange}
                  className="hidden"
                />
                <label
                  htmlFor="resume"
                  className="flex items-center justify-center gap-2 w-full px-4 py-4 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-[#5378BC] hover:bg-gray-50 transition-all"
                >
                  <Upload className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-500">
                    Click to upload resume (PDF, DOC, DOCX)
                  </span>
                </label>
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-md">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#284D93]" />
                  <span className="text-sm text-gray-700">{resumeFile.name}</span>
                  <span className="text-xs text-gray-500">
                    ({(resumeFile.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                </div>
                <button
                  type="button"
                  onClick={removeResume}
                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Accepted formats: PDF, DOC, DOCX (max{" "}
              {fields.resume.maxSizeMB || 10}MB)
            </p>
          </div>
        )}

        {/* Privacy Consent */}
        <div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              {...register("privacyConsent")}
              className={`mt-1 h-4 w-4 rounded border-gray-300 text-[#284D93] focus:ring-[#5378BC] cursor-pointer ${
                errors.privacyConsent ? "border-red-500" : ""
              }`}
            />
            <span className="text-sm text-gray-600">
              By submitting this application, I agree to RevealSite&apos;s{" "}
              <Link
                href="/privacy"
                target="_blank"
                className="text-[#284D93] underline hover:text-[#5378BC] transition-colors"
              >
                Privacy Policy
              </Link>{" "}
              and{" "}
              <Link
                href="/cookies"
                target="_blank"
                className="text-[#284D93] underline hover:text-[#5378BC] transition-colors"
              >
                Cookie Policy
              </Link>
              , and consent to the processing of my personal data for recruitment purposes.
            </span>
          </label>
          {errors.privacyConsent && (
            <p className="mt-1 text-sm text-red-600">
              {errors.privacyConsent.message}
            </p>
          )}
        </div>

        {/* Submit Button */}
        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full btn btn-primary btn-primary-hover-effect py-3 disabled:opacity-50 cursor-pointer transition-all duration-300 relative overflow-hidden group before:content-[''] before:absolute before:top-0 before:left-[-100%] before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent before:transition-[left] before:duration-500 hover:before:left-[100%] disabled:hover:before:left-[-100%] disabled:hover:!scale-100 ${
              isSubmitting ? "!cursor-not-allowed" : ""
            }`}
          >
            <span className="relative z-10 transition-all duration-300 text-white group-hover:text-white">
              {isSubmitting ? "Submitting Application..." : "Submit Application"}
            </span>
          </button>
        </div>
      </form>
    </div>
  );
}
